import { useVideoPlayer } from "../../../../gamePage/Contexts/contexts";
import { useState, useEffect, useContext } from "react";
import { _ } from "i18n";

interface TimeInputProps {
    value: number;
    onChange: (newValue: number) => void;
}

function secondsToTimeString(time: number): string {
    let totalSeconds = Math.max(0, time);
    const maxSeconds = 23 * 3600 + 59 * 60 + 59;
    if (totalSeconds > maxSeconds) { totalSeconds = maxSeconds; }
    totalSeconds = Math.floor(totalSeconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function timeStringToSeconds(time: string): number {
    const parts = time.split(":");
    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    if (parts.length === 2) {
        minutes = Number.parseInt(parts[0], 10);
        seconds = Number.parseInt(parts[1], 10);
    } else if (parts.length === 3) {
        hours = Number.parseInt(parts[0], 10);
        minutes = Number.parseInt(parts[1], 10);
        seconds = Number.parseInt(parts[2], 10);
    } else {
        return 0;
    }
    if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) {
        return 0;
    }
    return hours * 3600 + minutes * 60 + seconds;
}

export function TimeInput({ value, onChange }: TimeInputProps) {
    const { playerRef } = useVideoPlayer();
    const [internalValue, setInternalValue] = useState(secondsToTimeString(value));

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        setInternalValue(secondsToTimeString(value));
    }, [value, playerRef?.current?.currentTime]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        if (/^[0-9:]*$/.test(newValue)) {
            if (newValue.length <= 8) {
                setInternalValue(newValue);
            }
        }
    };

    const handleBlur = () => {
        const regexHHMMSS = /^(\d{1,2}):(\d{1,2}):(\d{1,2})$/;
        const regexMMSS = /^(\d{1,2}):(\d{1,2})$/;
        let hours = 0;
        let minutes = 0;
        let seconds = 0;
        let match = internalValue.match(regexHHMMSS);
        if (match) {
            hours = Number.parseInt(match[1], 10);
            minutes = Number.parseInt(match[2], 10);
            seconds = Number.parseInt(match[3], 10);
        } else {
            match = internalValue.match(regexMMSS);
            if (match) {
                minutes = Number.parseInt(match[1], 10);
                seconds = Number.parseInt(match[2], 10);
            } else {
                setInternalValue(secondsToTimeString(value));
                return;
            }
        }

        if (hours > 23) { hours = 23; }
        if (minutes > 59) { minutes = 59; }
        if (seconds > 59) { seconds = 59; }

        const totalSeconds = hours * 3600 + minutes * 60 + seconds;
        const newTimeString = secondsToTimeString(totalSeconds);
        setInternalValue(newTimeString);
        onChange(totalSeconds);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
        if (allowedKeys.includes(e.key)) { return; }
        if (/^[0-9]$/.test(e.key)) { return; }
        if (e.key === ":") {
            const colonCount = (internalValue.match(/:/g) || []).length;
            if (colonCount < 2) { return; }
        }
        e.preventDefault();
    };

    const increment = () => {
        let secs = timeStringToSeconds(internalValue);
        secs += 1;
        const newTimeString = secondsToTimeString(secs);
        setInternalValue(newTimeString);
        onChange(secs);
    };

    const decrement = () => {
        let secs = timeStringToSeconds(internalValue);
        secs = Math.max(0, secs - 1);
        const newTimeString = secondsToTimeString(secs);
        setInternalValue(newTimeString);
        onChange(secs);
    };

    return (
        <div className="createForm-timeInput">
            <input
                className="createForm-inputField"
                type="text"
                value={internalValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={_("gamePage.create.time.placeholder")}
            />
            <div className="createForm-timeArrows">
                <div onClick={increment} style={{ padding: "0 4px" }}>
                    ▲
                </div>
                <div onClick={decrement} style={{ padding: "0 4px" }}>
                    ▼
                </div>
            </div>
        </div>
    );
}
